/**
 * 3D Model.
 *
 * Written when no validated mesh artifact existed anywhere in this system and
 * the model branch was unreachable, which is why it drew an icon and offered a
 * download. That premise expired the day the RTX worker delivered an accepted
 * `model_glb`, and the screen kept its placeholder: it announced a model it
 * would not show, and described it with the *mesh* stage's numbers - the
 * full-resolution Poisson surface, hundreds of megabytes, not the twelve-odd
 * megabyte GLB a browser can open.
 *
 * Both halves of that are the same mistake. `mesh` and `model` are separate
 * stages on purpose: a mesh is a surface, a model is that surface packaged
 * into a GLB. This screen reads `model` for everything - the artifact it
 * fetches, the size it prints, the counts it prints - so what is described and
 * what is drawn are the same file.
 *
 * The download stays, as the secondary action. Some models are too large to
 * draw and some browsers cannot draw any, and in both cases the file is still
 * the deliverable.
 */

import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getLatestReconstruction, getScaleCalibration } from "@/api/client";
import type { ReconstructionJob } from "@/api/types";
import { ErrorState } from "@/components/ErrorState";
import { ModelViewer } from "./ModelViewer";
import { MeasurePanel, useMeasureState } from "./MeasurePanel";
import { MeshHealthPanel } from "./MeshHealthPanel";
import { useModelMesh } from "./useModelMesh";
import { hasCompletedSurface, provenanceIsUnrecorded } from "./viewerProvenance";
import { IconCube } from "@/components/icons";
import { InspectorPanel, Metric, PageHeader } from "@/components/product";
import { Button, Callout, EmptyState, Loading } from "@/components/ui";
import { RECONSTRUCTION_TERMS } from "@/i18n/terms";
import { useAsync } from "@/lib/useAsync";
import { useProjectContext } from "@/features/projects/ProjectScreen";
import { useOptionalAuthUser } from "@/features/auth/AuthGate";
import { downloadArtifact, downloadFailureText } from "./artifactDownload";
import { paths } from "@/app/paths";
import "./reconstruction.css";

/**
 * Five states, and each one comes from `model_state` rather than from the job.
 *
 * The server derives that from validated artifacts, so the rule this screen has
 * to obey - **a mesh exists when a validated mesh artifact exists** - is
 * enforced in one place instead of being restated here. A succeeded job whose
 * dense stage never ran is still a succeeded job, and reading `status` would
 * announce a model nobody can open.
 */
type ModelView = "none" | "sparse" | "dense" | "mesh" | "textured";

function viewFor(job: ReconstructionJob | null): ModelView {
  if (job === null) return "none";
  const model = job.model_state;
  if (model.texture.state === "ready") return "textured";
  // `has_model` is the only predicate licensing "3D Model hazır".
  if (model.has_model) return "mesh";
  if (model.dense.state === "ready") return "dense";
  if (model.sparse.state === "ready") return "sparse";
  return "none";
}

export function ProjectModelTab() {
  const { project } = useProjectContext();
  const navigate = useNavigate();
  const fetcher = useCallback(
    (signal: AbortSignal) => getLatestReconstruction(project.id, { signal }),
    [project.id],
  );
  const { state } = useAsync(fetcher);

  const job = state.status === "success" ? state.data : null;
  /* Held here rather than inside the panel: the viewer draws the markers and
     the panel reads them, so the state belongs to their common parent. */
  const measure = useMeasureState();
  const view = viewFor(job);
  const model = job?.model_state ?? null;

  // The GLB, by the id the server put on the `model` stage. Above the early
  // returns because a hook must run on every render; null ids keep it idle,
  // which is the same thing the early returns would have achieved and is
  // allowed to change from render to render.
  // The viewer's own identity goes into the mesh cache key, so leaving this
  // screen and coming back skips the parse - and so a second account on the
  // same browser profile cannot be handed the first one's geometry.
  const user = useOptionalAuthUser();
  //: The stored calibration, so every sentence about scale on this screen is
  //: about *this* model rather than about photogrammetry in general.
  //:
  //: Keyed on the job's *id*, never on the job object: `job` is derived from
  //: the async state and is a fresh reference on every render, so a fetcher
  //: closed over it is a new function each time and `useAsync` re-fires
  //: forever. That loop starved the mesh request, which is how it was found.
  const jobId = job?.id ?? null;
  const scale = useAsync(
    useCallback(
      (signal: AbortSignal) =>
        jobId ? getScaleCalibration(jobId, { signal }) : Promise.resolve(null),
      [jobId],
    ),
  ).state;
  const { state: meshState, retry: retryMesh } = useModelMesh(
    job?.id ?? null,
    model?.model.artifact_id ?? null,
    model?.model.byte_size ?? null,
    user?.id ?? null,
  );

  const missing = state.status === "error" && state.error.status === 404;
  if (state.status === "loading" && state.data === null) return <Loading />;
  if (state.status === "error" && !missing) return <ErrorState error={state.error} />;

  return (
    <div className="stack">
      <PageHeader title="3D Model" subtitle={project.name} />

      {(view === "mesh" || view === "textured") && model ? (
        <div className="stack">
          {/*
            The model is the screen, not a panel on it. It used to share a
            two-column grid with an inspector, so the thing the customer waited
            an hour for occupied less space than its own measurements.
          */}
          <section className="model-hero">
            {meshState.status === "ready" ? (
              <ModelViewer mesh={meshState.mesh} measurement={measure} />
            ) : (
              <div className="viewport__inner">
                <IconCube size={40} />
                {/*
                  A spinner over a black canvas reads as "broken". Saying what
                  is happening costs one line.
                */}
                <p className="viewport__state">
                  {meshState.status === "loading"
                    ? "3D model yükleniyor…"
                    : meshState.status === "too-large"
                      ? "Bu model tarayıcıda gösterilemeyecek kadar büyük."
                      : meshState.status === "empty"
                        ? "Model dosyası boş görünüyor."
                        : "3D model görüntülenemedi."}
                </p>
                {meshState.status === "loading" && (
                  <p className="viewport__note">
                    Dosya boyutuna göre bu birkaç saniye sürebilir.
                  </p>
                )}
                {meshState.status === "failed" && (
                  <p className="viewport__note" role="alert">
                    {meshState.message}
                  </p>
                )}
                {/*
                  Present in every one of those states. A model too large to
                  draw, or a browser that cannot draw one, is still a model
                  somebody is entitled to have.
                */}
                <div className="viewport__actions">
                  {meshState.status === "failed" && (
                    <Button onClick={retryMesh}>Yeniden dene</Button>
                  )}
                  <ModelDownloadButton
                    jobId={job?.id ?? null}
                    artifactId={model.model.artifact_id}
                  />
                </div>
              </div>
            )}
          </section>

          {meshState.status === "ready" && job && (
            <MeasurePanel jobId={job.id} mesh={meshState.mesh} measure={measure} />
          )}

          <div className="row model-hero__actions">
            <p className="model-hero__state">
              {view === "textured" ? "Dokulu 3D modeliniz hazır." : "3D modeliniz hazır."}
            </p>
            {meshState.status === "ready" && (
              <ModelDownloadButton
                jobId={job?.id ?? null}
                artifactId={model.model.artifact_id}
              />
            )}
            <Button onClick={() => navigate(paths.projectFiles(project.id))}>
              Tüm çıktılar
            </Button>
          </div>

          {/*
            Read from the stored calibration rather than assumed. This
            sentence used to say a model is never in real units, which stopped
            being true when the measurement panel shipped and went on being
            printed to customers who had just calibrated their own model.
          */}
          <Callout tone="neutral">
            {RECONSTRUCTION_TERMS.scaleNote(scale.data?.verified === true)}
          </Callout>

          {/*
            Print readiness, beside the scale note and never merged into it.
            Two truths that fail independently get two rows.
          */}
          {job?.id && <MeshHealthPanel jobId={job.id} />}

          {/*
            The measurements, one disclosure away. "Vertex" and "Face" are the
            renderer's words, and neither is a decision a customer makes about
            their own model.
          */}
          <details className="run-technical">
            <summary>Teknik ayrıntılar</summary>
            <InspectorPanel title="Model">
              <Metric
                label="Köşe sayısı"
                value={
                  (meshState.status === "ready"
                    ? meshState.mesh.vertexCount
                    : model.model.vertices
                  )?.toLocaleString("tr") ?? null
                }
              />
              <Metric
                label="Yüzey sayısı"
                value={
                  (meshState.status === "ready"
                    ? meshState.mesh.faceCount
                    : model.model.faces
                  )?.toLocaleString("tr") ?? null
                }
              />
              <Metric
                label="Dosya boyutu"
                value={
                  model.model.byte_size
                    ? `${(model.model.byte_size / 1e6).toFixed(1)} MB`
                    : null
                }
              />
              <Metric
                label="Doku"
                value={view === "textured" ? "Var" : "Yok"}
                tone={view === "textured" ? "success" : "neutral"}
              />
              <Metric
                label="Ölçek"
                value={
                  scale.data?.verified
                    ? RECONSTRUCTION_TERMS.scaleVerified
                    : RECONSTRUCTION_TERMS.scaleUncalibrated
                }
                tone={scale.data?.verified ? "success" : "caution"}
              />
              {/*
                Where the surface came from, beside what it is made of. A model
                whose file carries no record reads as "kaydı olmayan", which is
                not the same as invented - see `viewerProvenance`.
              */}
              {meshState.status === "ready" && (
                <Metric
                  label="Geometri güveni"
                  value={
                    provenanceIsUnrecorded(meshState.mesh)
                      ? "Kaydı yok"
                      : hasCompletedSurface(meshState.mesh)
                        ? "Yapay tamamlama içeriyor"
                        : "Tümü ölçülen yüzey"
                  }
                  tone={
                    provenanceIsUnrecorded(meshState.mesh)
                      ? "neutral"
                      : hasCompletedSurface(meshState.mesh)
                        ? "caution"
                        : "success"
                  }
                />
              )}
            </InspectorPanel>
          </details>
        </div>
      ) : view === "dense" && model ? (
        <div className="stack">
          {/*
            A dense cloud is millions of points and still not a surface. Saying
            so is the whole reason `dense` is a separate state rather than a
            step on the way to claiming a model.
          */}
          <EmptyState
            title="Yoğun nokta bulutu hazır."
            description="Bu, çok sayıda 3D noktadır; bir yüzey modeli değildir. Yüzey modeli henüz oluşturulmadı."
            action={
              <Button variant="primary" onClick={() => navigate(paths.projectFiles(project.id))}>
                Çıktıları görüntüle
              </Button>
            }
          />
          {model.dense.points && (
            <Callout tone="neutral">
              {model.dense.points.toLocaleString("tr")} nokta ölçüldü.
            </Callout>
          )}
        </div>
      ) : view === "sparse" ? (
        <div className="stack">
          <EmptyState
            title="Kamera konumları çözüldü; yüzey modeli henüz yok."
            description="Elinizdeki sonuç, kameraların çözdüğü 3D noktalardır. Bu bir yüzey modeli değildir."
            action={
              <>
                <Button variant="primary" onClick={() => navigate(paths.projectReconstruction(project.id))}>
                  Yüzey modeli oluştur
                </Button>
                <Button onClick={() => navigate(paths.projectFiles(project.id))}>
                  Çıktıları görüntüle
                </Button>
              </>
            }
          />
          {/*
            Why there is no model, in the words that match the actual reason.
            `unavailable` is this deployment's limit; `failed` is a run that
            tried. Telling somebody to reshoot because a server has no GPU
            would waste their afternoon.
          */}
          <Callout tone="neutral">
            {model?.dense.state === "failed"
              ? "Yoğun aşama bu çekimde başarısız oldu. Farklı açılardan ek çekim gerekebilir."
              : RECONSTRUCTION_TERMS.denseUnavailable}
          </Callout>
        </div>
      ) : (
        // No geometry at all. What to say depends on what the last run is
        // doing - "önce bir rekonstrüksiyon çalıştırılmalı" was shown even to
        // somebody whose run was at that moment in progress.
        <EmptyState
          title={
            job?.status === "running"
              ? "3D çalışması sürüyor."
              : job?.status === "queued"
                ? "3D çalışması sırada."
                : job?.status === "failed"
                  ? "Son 3D çalışması tamamlanamadı."
                  : "Henüz bir 3D sonuç yok."
          }
          description={
            job
              ? "Durumu ve sonraki adımı 3D Rekonstrüksiyon ekranında görebilirsiniz."
              : "Önce bir 3D çalışması başlatın."
          }
          action={
            <Button variant="primary" onClick={() => navigate(paths.projectReconstruction(project.id))}>
              3D Rekonstrüksiyona git
            </Button>
          }
        />
      )}
    </div>
  );
}

/**
 * The model file itself, through the session.
 *
 * The screen used to send people to the outputs table to fetch the thing they
 * were already looking at. This is the same authenticated delivery that table
 * uses - no second mechanism, and no URL that can be copied out of the page.
 */
function ModelDownloadButton({
  jobId,
  artifactId,
}: {
  jobId: string | null;
  artifactId: string | null;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  if (!jobId || !artifactId) return null;
  return (
    <span className="stack-tight">
      <Button
        busy={busy}
        onClick={async () => {
          if (busy) return;
          setBusy(true);
          setFailed(null);
          try {
            await downloadArtifact(jobId, artifactId, "model.glb");
          } catch (error) {
            setFailed(downloadFailureText(error));
          } finally {
            setBusy(false);
          }
        }}
      >
        3D modeli indir
      </Button>
      {failed && (
        <span className="muted small" role="alert">
          {failed}
        </span>
      )}
    </span>
  );
}
